import { ApiError } from '../types/api';

// Maps POST /admin/login failures (already normalized into an ApiError by
// api.ts's response interceptor) to what Login.tsx should show. See
// sb-express-app's AdminRoutes.js / LoginLockout.js:
//   401 { message: 'Invalid credentials' } — generic, on every failure path.
//   423 { message, retryAfter } — locked after MAX_FAILED_ATTEMPTS (5) bad
//     passwords, for LOCKOUT_MS (900s / 15 min) — used as the fallback below
//     when a 423 arrives without a retryAfter.
const DEFAULT_LOCKOUT_SECONDS = 15 * 60;

export type LoginErrorResult =
  | { kind: 'locked'; retryAfterSeconds: number }
  | { kind: 'message'; message: string };

function asApiError(error: unknown): Partial<ApiError> {
  return typeof error === 'object' && error !== null ? (error as Partial<ApiError>) : {};
}

// Named so the copy lives in one place (matches PASSWORD_POLICY_MESSAGE's
// pattern) instead of being composed inline at the call site.
export function formatLockoutMessage(retryAfterSeconds: number): string {
  const minutes = Math.max(1, Math.ceil(retryAfterSeconds / 60));
  return `Account locked due to repeated failed logins. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
}

export function mapLoginError(error: unknown): LoginErrorResult {
  const { status, message, retryAfter } = asApiError(error);

  if (status === 423) {
    return { kind: 'locked', retryAfterSeconds: retryAfter ?? DEFAULT_LOCKOUT_SECONDS };
  }

  return {
    kind: 'message',
    message: typeof message === 'string' && message ? message : 'Login failed',
  };
}
